import styled from 'styled-components'
import { theme } from '../../theme/theme'
import { BodyTiny } from '../styles/Typography'
import { ChartTooltip, PLOT_SURFACE_GAP } from './shared'
import { ChartStep } from './type'

export const SEGMENT_STACK_THICKNESS = 20
// Below this share the segment is too narrow to hold its own number legibly.
const INLINE_LABEL_MIN_SHARE = 0.12

const PERCENT_UNIT = '%'

const formatWithUnit = (value: number, unit: string): string =>
  unit === PERCENT_UNIT ? `${value}${unit}` : `${value} ${unit}`

export const sumBySteps = (steps: readonly ChartStep[], values: Record<string, number>): number =>
  steps.reduce((sum, { key }) => sum + (values[key] ?? 0), 0)

type Props = {
  steps: readonly ChartStep[]
  values: Record<string, number>
  /** Unit for the tooltip, e.g. "personer". */
  unit: string
  /** Lets rows on a shared scale sit at less than the full width. */
  widthPercent?: number
  /** Appends the unit to the inline segment label too, e.g. "91%" instead of "91". */
  showUnitInline?: boolean
  /** Off for bars read as a shape, where the total beside the bar carries the number instead. */
  showInlineValues?: boolean
  /** Denominator for the segment widths; defaults to the steps drawn. Pass the row's own total
   * so undrawable bands leave a gap instead of widening the rest. */
  scaleTotal?: number
}

export const SegmentStack = ({
  steps,
  values,
  unit,
  widthPercent = 100,
  showUnitInline = false,
  showInlineValues = true,
  scaleTotal,
}: Props) => {
  const total = scaleTotal ?? sumBySteps(steps, values)
  // Empty segments are dropped so the rounded end lands on the last visible one.
  const visibleSteps = steps.filter(({ key }) => (values[key] ?? 0) > 0)
  // A single step carries no series name worth repeating, so its tooltip is just the amount.
  const showStepName = steps.length > 1

  return (
    <Stack $widthPercent={widthPercent}>
      {visibleSteps.map(({ key, name, color, ink }, index) => {
        const value = values[key] ?? 0
        return (
          <Segment
            key={key}
            $share={total > 0 ? (value / total) * 100 : 0}
            $color={color}
            $isFirst={index === 0}
            $isLast={index === visibleSteps.length - 1}
          >
            {showInlineValues && value / total > INLINE_LABEL_MIN_SHARE && (
              <SegmentValue $color={ink}>{showUnitInline ? formatWithUnit(value, unit) : value}</SegmentValue>
            )}
            <ChartTooltip>
              {showStepName && `${name}: `}
              {formatWithUnit(value, unit)}
            </ChartTooltip>
          </Segment>
        )
      })}
    </Stack>
  )
}

const Stack = styled.div<{ $widthPercent: number }>`
  display: flex;
  gap: ${PLOT_SURFACE_GAP}px;
  height: ${SEGMENT_STACK_THICKNESS}px;
  width: ${({ $widthPercent }) => $widthPercent}%;
`

// flex-basis, not flex-grow: shares adding up to less than the whole must stay short.
const Segment = styled.div<{ $share: number; $color: string; $isFirst: boolean; $isLast: boolean }>`
  position: relative;
  flex: 0 1 ${({ $share }) => $share}%;
  background-color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ $isFirst, $isLast }) =>
    `${$isFirst ? '4px' : '0'} ${$isLast ? '4px' : '0'} ${$isLast ? '4px' : '0'} ${$isFirst ? '4px' : '0'}`};

  &:hover > ${ChartTooltip} {
    opacity: 1;
  }
`

const SegmentValue = styled(BodyTiny)<{ $color?: string }>`
  color: ${({ $color }) => $color ?? theme.color.black};
  font-weight: 500;
  font-variant-numeric: tabular-nums;
`
